import Popper from '@mui/material/Popper';
import React from 'react';
import parse from 'autosuggest-highlight/parse';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  getComponentByNumericalId,
  isAddressComponent,
  isGoogleAddressComponent,
  getAdmiralFieldAnswer,
} from '../__helpers__/component.helpers';
import { GenericAdmiralFields } from '../../Data/Components';

const filterOptions = createFilterOptions();

/**
 * isThisLastAddressComponentOnPage
 * @returns boolean
 * @description Checks if the current Google address component is the last one on the page - this is required as we only want to attempt an auto advance is there is more than one Google address component on the page and we are selecting an option
 */
export const isThisLastAddressComponentOnPage = ({
  components,
  componentId,
}) => {
  // Get the component with the given componentId
  const targetComponent = getComponentByNumericalId({
    components,
    componentId,
  });

  if (!targetComponent) {
    throw new Error(`Component with id ${componentId} not found`);
  }

  const targetPageId = targetComponent.component.page;

  const addressComponentsOnTargetPage = Object.values(components).filter(
    (component) => {
      return (
        (isGoogleAddressComponent(component) ||
          isAddressComponent(component)) &&
        component.component.page === targetPageId
      );
    }
  );

  // If there are no Google address components, return false
  if (addressComponentsOnTargetPage.length === 0) {
    return false;
  }

  // If there is only one Google address component, it is the last by default
  if (addressComponentsOnTargetPage.length === 1) {
    return addressComponentsOnTargetPage[0].component.id === componentId;
  }

  // Find the index of the target component among the Google address components
  const targetIndex = addressComponentsOnTargetPage.findIndex(
    (componentWrapper) => componentWrapper.component.id === componentId
  );

  // Check if the target component is the last one in the list
  return targetIndex === addressComponentsOnTargetPage.length - 1;
};

export const createAutoCompletePopper = (autoCompleteRef) => {
  const AutoCompletePopper = (props) => {
    const inputWrapper =
      autoCompleteRef.current.getElementsByClassName('MuiInputBase-root')[0];
    return (
      // eslint-disable-next-line react/jsx-filename-extension
      <Popper
        {...props}
        style={{
          width: inputWrapper ? inputWrapper.clientWidth : null,
        }}
        anchorEl={inputWrapper}
      />
    );
  };

  return AutoCompletePopper;
};

/**
 * renderMainTextWithHighlighting
 * @param {Object} option
 * @returns {React.ReactNode}
 * @description Renders an option's main text with highlighting
 */
export const renderMainTextWithHighlighting = (option) => {
  // This splits up the main text into parts based on if they should be highlighted or not.
  const mainTextHighlightingMatches = option?.mainText?.matches || [];
  const mainTextHighlightingParts = parse(
    option?.mainText?.text || '',
    mainTextHighlightingMatches.map((match) => [
      match.startOffset || 0,
      match.endOffset || option?.mainText?.text?.length || 0,
    ])
  );

  return mainTextHighlightingParts.map((part) => (
    <span key={part.text} style={{ fontWeight: part.highlight ? 700 : 400 }}>
      {part.text}
    </span>
  ));
};

/**
 * This filter will limit options when the input is a subset of the options but not hide the options completely when the input is more detailed.
 * Without this, if the customer enters a values which is more detailed than the options available then we will fail to show any options.
 */
export const showAllOptionsWhenNoMatches = (optionsArray, state) => {
  const filtered = filterOptions(optionsArray, state);
  // If no options match the input, return all options
  if (filtered.length === 0 && optionsArray.length > 0) {
    return optionsArray;
  }
  return filtered;
};

/**
 * Finds filled zipcode field for location bias
 * @param {Object} components - All components from the Redux store
 * @returns {Object|null} Object with zipcode value or null if no field is filled
 */
export const getAddressFieldsBias = (components) => {
  if (!components) return null;

  const zipcodeValue = getAdmiralFieldAnswer(
    components,
    GenericAdmiralFields.PRIMARY_ADDRESS_POSTCODE
  );

  if (!zipcodeValue) {
    return null;
  }

  return {
    zipcodeValue,
  };
};
